import { computed, ref } from 'vue';

const TIMEZONE_STORAGE_KEY = 'overpassPlanner.timezone';

type TimeZonePreference = 'local' | string;

function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function isValidTimeZone(value: string): boolean {
  try {
    // Validate IANA timezone names.
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-GB', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function parseStoredPreference(raw: string | null): TimeZonePreference {
  if (!raw) return 'local';
  if (raw === 'local') return 'local';
  return isValidTimeZone(raw) ? raw : 'local';
}

function getOffsetMsForTimeZone(epochMs: number, timeZone: string): number {
  if (timeZone === 'UTC') return 0;
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    timeZoneName: 'shortOffset',
    hour: '2-digit',
  });
  const parts = fmt.formatToParts(new Date(epochMs));
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const match = tzName.match(/^GMT([+-]\d{1,2})(?::?(\d{2}))?$/);
  if (!match) return 0;
  const hours = Number.parseInt(match[1], 10);
  const mins = Number.parseInt(match[2] ?? '0', 10);
  const sign = hours < 0 ? -1 : 1;
  return (hours * 60 + sign * mins) * 60_000;
}

const stored = typeof localStorage !== 'undefined'
  ? localStorage.getItem(TIMEZONE_STORAGE_KEY)
  : null;

const timeZonePreference = ref<TimeZonePreference>(parseStoredPreference(stored));

export function parseUtcEpochMs(value: string): number {
  const raw = String(value ?? '').trim();
  if (!raw) return Number.NaN;
  if (/[zZ]$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(raw)) return Date.parse(raw);
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  return Date.parse(`${normalized}Z`);
}

function getParts(epochMs: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(epochMs));
  const byType = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: byType('year'),
    month: byType('month'),
    day: byType('day'),
    hour: byType('hour'),
    minute: byType('minute'),
  };
}

function toDatetimeLocalWithTimeZone(epochMs: number, timeZone: string): string {
  const p = getParts(epochMs, timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

function parseDatetimeLocalWithTimeZone(value: string, timeZone: string): number {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return Number.NaN;
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  const hour = Number.parseInt(match[4], 10);
  const minute = Number.parseInt(match[5], 10);

  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  const offset1 = getOffsetMsForTimeZone(utcGuess, timeZone);
  const candidate = utcGuess - offset1;
  const offset2 = getOffsetMsForTimeZone(candidate, timeZone);
  return utcGuess - offset2;
}

export function useDateTimeSettings() {
  const browserTimeZone = computed(() => getBrowserTimeZone());

  const effectiveTimeZone = computed(() => (
    timeZonePreference.value === 'local'
      ? browserTimeZone.value
      : timeZonePreference.value
  ));

  const setTimeZonePreference = (value: TimeZonePreference) => {
    timeZonePreference.value = value === 'local' ? 'local' : (isValidTimeZone(value) ? value : 'local');
    localStorage.setItem(TIMEZONE_STORAGE_KEY, timeZonePreference.value);
  };

  const formatDateTime = (value: string | number): string => {
    const ms = typeof value === 'number' ? value : parseUtcEpochMs(value);
    if (!Number.isFinite(ms)) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: effectiveTimeZone.value,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(ms));
  };

  const formatDateShort = (epochMs: number): string => {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: effectiveTimeZone.value,
      day: '2-digit',
      month: 'short',
    }).format(new Date(epochMs));
  };

  const formatTimeShort = (epochMs: number): string => {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: effectiveTimeZone.value,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(epochMs));
  };

  const getHourInEffectiveTimeZone = (epochMs: number): number => {
    const hour = new Intl.DateTimeFormat('en-GB', {
      timeZone: effectiveTimeZone.value,
      hour: '2-digit',
      hour12: false,
    }).format(new Date(epochMs));
    return Number.parseInt(hour, 10);
  };

  const toDatetimeLocal = (epochMs: number): string => {
    return toDatetimeLocalWithTimeZone(epochMs, effectiveTimeZone.value);
  };

  const fromDatetimeLocal = (value: string): number => {
    return parseDatetimeLocalWithTimeZone(value, effectiveTimeZone.value);
  };

  const availableTimeZones = computed<string[]>(() => {
    const local = browserTimeZone.value;
    const supported = typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'];
    return [local, 'UTC', ...supported.filter((tz) => tz !== local && tz !== 'UTC')];
  });

  return {
    timeZonePreference,
    setTimeZonePreference,
    browserTimeZone,
    effectiveTimeZone,
    availableTimeZones,
    formatDateTime,
    formatDateShort,
    formatTimeShort,
    getHourInEffectiveTimeZone,
    toDatetimeLocal,
    fromDatetimeLocal,
  };
}
